import React, { useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import uuid from 'uuid';
import { Toolbar } from '@storable/canaveral-components';
import NavigationGroupForm from '../NavigationGroupForm';
import NavigationLinkForm from '../NavigationLinkForm';
import NavigationPagesMenuItem, { NavigationPagesMenuItemLoading } from '../NavigationPagesMenuItem';
import PagesMenu, { PagesMenuLoading } from '../PagesMenu';
import styles from './styles.scss';

const propTypes = {
  enablePagesSearch: PropTypes.bool,
  navigationPageIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onItemAdd: PropTypes.func.isRequired,
  onPagesSearch: PropTypes.func.isRequired,
  pages: PropTypes.arrayOf(PropTypes.object),
  pagesMenuTitle: PropTypes.string,
  pagesSearchError: PropTypes.string,
  searchingPages: PropTypes.bool,
  totalPages: PropTypes.number,
};

const defaultProps = {
  enablePagesSearch: undefined,
  pages: undefined,
  pagesMenuTitle: undefined,
  pagesSearchError: undefined,
  searchingPages: undefined,
  totalPages: undefined,
};

const MENU_ENUM = {
  PAGE: 'page',
  LINK: 'link',
  GROUP: 'group',
};

const NavigationSettingsControls = ({
  navigationPageIds,
  enablePagesSearch,
  onItemAdd,
  onPagesSearch,
  pages,
  pagesMenuTitle,
  pagesSearchError,
  searchingPages,
  totalPages,
  ...rest
}) => {
  const [menu, setMenu] = useState(MENU_ENUM.PAGE);

  const onPageSelect = () => setMenu(MENU_ENUM.PAGE);

  const onLinkSelect = () => setMenu(MENU_ENUM.LINK);

  const onGroupSelect = () => setMenu(MENU_ENUM.GROUP);

  const onCancel = () => onPageSelect();

  const onItemWithIdAdd = (navigationItem) => {
    onItemAdd({ ...navigationItem, id: uuid.v4() });
  };

  const onLinkSubmit = (navigationItem) => {
    onPageSelect();
    onItemWithIdAdd(navigationItem);
  };

  const onGroupSubmit = (navigationItem) => {
    onPageSelect();
    onItemWithIdAdd(navigationItem);
  };

  const onPagesMenuItemClick = (page) => {
    onItemWithIdAdd({ page });
  };

  const menuClasses = enablePagesSearch ? styles.menu : styles.menuWithoutSearch;

  return (
    <React.Fragment>
      <Toolbar className={styles.options}>
        <Toolbar.Item
          active={menu === MENU_ENUM.PAGE}
          onClick={onPageSelect}
        >
          Page
        </Toolbar.Item>
        <Toolbar.Item
          active={menu === MENU_ENUM.LINK}
          disabled={!pages}
          onClick={onLinkSelect}
        >
          Link
        </Toolbar.Item>
        <Toolbar.Item
          active={menu === MENU_ENUM.GROUP}
          disabled={!pages}
          onClick={onGroupSelect}
        >
        Group
        </Toolbar.Item>
      </Toolbar>
      {(() => {
        switch (menu) {
          case MENU_ENUM.LINK:
            return (
              <NavigationLinkForm
                onCancel={onCancel}
                onSubmit={onLinkSubmit}
              />
            );
          case MENU_ENUM.GROUP:
            return (
              <NavigationGroupForm
                onCancel={onCancel}
                onSubmit={onGroupSubmit}
              />
            );
          case MENU_ENUM.PAGE:
          default:
            return (
              <div className={styles.pages}>
                {pages ? (
                  <PagesMenu
                    {...rest}
                    componentClass={NavigationPagesMenuItem}
                    className={menuClasses}
                    enableSearch={enablePagesSearch}
                    error={pagesSearchError}
                    loading={searchingPages}
                    navigationPageIds={navigationPageIds}
                    onClick={onPagesMenuItemClick}
                    onSearch={onPagesSearch}
                    pages={pages}
                    title={pagesMenuTitle}
                    totalPages={totalPages}
                  />
                ) : (
                  <PagesMenuLoading
                    componentClass={NavigationPagesMenuItemLoading}
                    className={styles.menu}
                  />
                )}
              </div>
            );
        }
      })()}
    </React.Fragment>
  );
};

NavigationSettingsControls.propTypes = propTypes;
NavigationSettingsControls.defaultProps = defaultProps;

NavigationSettingsControls.fragments = {
  page: gql`
    fragment NavigationSettingsControlsPage on Page {
      ...NavigationPagesMenuItemPage
      ...PagesMenuPage
    }

    ${NavigationPagesMenuItem.fragments.page}
    ${PagesMenu.fragments.page}
  `,
};

export default NavigationSettingsControls;
